"use client";

import { Button } from "../../";
import { cn } from "../../utils";
import { IconArrowDown } from "@tabler/icons-react";
import type { ComponentProps } from "react";
import { useCallback } from "react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";

export type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, ...props }: ConversationProps) => (
  <StickToBottom
    className={cn("al-relative al-flex-1 al-overflow-y-hidden", className)}
    initial="smooth"
    resize="smooth"
    role="log"
    {...props}
  />
);

export type ConversationContentProps = ComponentProps<typeof StickToBottom.Content>;

export const ConversationContent = ({ className, ...props }: ConversationContentProps) => (
  <StickToBottom.Content
    className={cn("al-flex al-flex-col al-gap-8 al-p-4 al-px-0", className)}
    {...props}
  />
);

export type ConversationEmptyStateProps = ComponentProps<"div"> & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};

export const ConversationEmptyState = ({
  className,
  title = "No messages yet",
  description = "Start a conversation to see messages here",
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={cn(
      "al-flex al-size-full al-flex-col al-items-center al-justify-center al-gap-3 al-p-8 al-text-center",
      className
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="al-text-muted-foreground">{icon}</div>}
        <div className="al-space-y-1">
          <h3 className="al-font-medium al-text-sm">{title}</h3>
          {description && <p className="al-text-muted-foreground al-text-sm">{description}</p>}
        </div>
      </>
    )}
  </div>
);

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          "al-absolute al-bottom-4 al-left-[50%] al-translate-x-[-50%] al-rounded-full dark:al-bg-background dark:hover:al-bg-muted",
          className
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <IconArrowDown className="al-size-4" />
      </Button>
    )
  );
};
